import {
  createImplantationSchema,
  IMPLANTATION_OBJECTIVES,
  META_BUSINESS_VERTICAL_LABELS,
  META_BUSINESS_VERTICALS,
  type CreateImplantationInput,
  type ImplantationDto,
  type MetaBusinessVertical,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, Rocket } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";

export const Route = createFileRoute("/app/$workspaceId/implantacao")({
  component: ImplantacaoPage,
});

export const STATUS_LABEL: Record<ImplantationDto["status"], string> = {
  em_andamento: "Em andamento",
  aguardando_manual: "Aguardando etapas manuais",
  concluida: "Concluída",
  com_erro: "Com erro",
};

const OBJECTIVE_LABEL: Record<(typeof IMPLANTATION_OBJECTIVES)[number], string> = {
  OUTCOME_LEADS: "Leads (contatos)",
  OUTCOME_TRAFFIC: "Tráfego para o site",
  OUTCOME_SALES: "Vendas",
  OUTCOME_ENGAGEMENT: "Engajamento",
};

function implantationsQuery(workspaceId: string) {
  return {
    queryKey: ["workspaces", workspaceId, "implantations"],
    queryFn: () => api<ImplantationDto[]>(`/api/workspaces/${workspaceId}/implantations`),
  };
}

function ImplantacaoPage() {
  const { workspaceId } = Route.useParams();
  const [novo, setNovo] = useState(false);
  const list = useQuery(implantationsQuery(workspaceId));

  if (novo) return <NovaImplantacao workspaceId={workspaceId} onCancel={() => setNovo(false)} />;

  return (
    <>
      <PageHeader
        title="Implantação de cliente"
        description="Preencha uma vez: o sistema cria na Meta tudo o que a API permite e deixa o resto como tarefa com link."
        actions={
          <Button onClick={() => setNovo(true)}>
            <Plus />
            Nova implantação
          </Button>
        }
      />
      <Card>
        <CardContent>
          {list.isPending ? (
            <LoadingState rows={3} />
          ) : list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : list.data.length === 0 ? (
            <EmptyState
              icon={<Rocket className="size-8" aria-hidden />}
              title="Nenhuma implantação ainda"
              description="Comece por um cliente novo: página, BM, conta de anúncio, pixel e a primeira campanha."
              action={<Button onClick={() => setNovo(true)}>Nova implantação</Button>}
            />
          ) : (
            <ul className="divide-y">
              {list.data.map((i) => {
                const feitas = i.steps.filter(
                  (s) => s.status === "concluido" || s.status === "pulado",
                ).length;
                return (
                  <li key={i.id} className="flex items-center justify-between gap-4 py-3">
                    <div>
                      <Link
                        to="/app/$workspaceId/implantacao/$implantationId"
                        params={{ workspaceId, implantationId: i.id }}
                        className="font-medium hover:underline"
                      >
                        {i.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {feitas} de {i.steps.length} etapas ·{" "}
                        {new Date(i.createdAt).toLocaleDateString("pt-BR")}
                      </p>
                    </div>
                    <Badge variant={i.status === "concluida" ? "default" : "secondary"}>
                      {STATUS_LABEL[i.status]}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">{children}</CardContent>
    </Card>
  );
}

function Choice<T extends string>({
  id,
  value,
  onChange,
  options,
  placeholder,
}: {
  id: string;
  value: T | "";
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  placeholder: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as T)}>
      <SelectTrigger id={id}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const SEM_PAGINA = "__sem_pagina__";
const NOVO = "__novo__";

function NovaImplantacao({ workspaceId, onCancel }: { workspaceId: string; onCancel: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const connections = useQuery(queries.metaConnections(workspaceId));
  const pages = useQuery(queries.pages(workspaceId));
  const bms = useQuery(queries.businesses(workspaceId));
  const accounts = useQuery(queries.adAccounts(workspaceId));

  const [name, setName] = useState("");
  const [connectionId, setConnectionId] = useState("");
  const [page, setPage] = useState<string>(SEM_PAGINA);
  const [bmChoice, setBmChoice] = useState<string>(NOVO);
  const [bmName, setBmName] = useState("");
  const [vertical, setVertical] = useState<MetaBusinessVertical | "">("");
  const [accountChoice, setAccountChoice] = useState<string>(NOVO);
  const [accountName, setAccountName] = useState("");
  const [pixelCreate, setPixelCreate] = useState(true);
  const [pixelName, setPixelName] = useState("");
  const [domain, setDomain] = useState("");
  const [campCreate, setCampCreate] = useState(true);
  const [camp, setCamp] = useState({
    name: "",
    objective: "OUTCOME_LEADS" as (typeof IMPLANTATION_OBJECTIVES)[number],
    daily: "40",
    link: "",
    primaryText: "",
    headline: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const userConnections = (connections.data ?? []).filter(
    (c) => c.status === "ativa" && c.kind !== "system_user",
  );
  const adminBms = (bms.data ?? []).filter((b) => b.permittedRoles.includes("ADMIN"));

  const buildInput = (): unknown => ({
    name,
    connectionId,
    pageMetaId: page === SEM_PAGINA ? null : page,
    business:
      bmChoice === NOVO
        ? { mode: "novo", name: bmName, vertical: vertical || undefined }
        : { mode: "existente", businessId: bmChoice },
    adAccount:
      accountChoice === NOVO
        ? { mode: "nova", name: accountName }
        : { mode: "existente", adAccountMetaId: accountChoice },
    pixel: { create: pixelCreate, name: pixelName },
    domain: domain
      .trim()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, ""),
    campaign: {
      create: campCreate,
      name: camp.name,
      objective: camp.objective,
      dailyBudgetCents: Math.round(Number(camp.daily.replace(",", ".") || "0") * 100),
      link: camp.link,
      primaryText: camp.primaryText,
      headline: camp.headline,
    },
  });

  const criar = useMutation({
    mutationFn: (input: CreateImplantationInput) =>
      api<ImplantationDto>(`/api/workspaces/${workspaceId}/implantations`, {
        method: "POST",
        body: input,
      }),
    onSuccess: async (impl) => {
      await queryClient.invalidateQueries({
        queryKey: ["workspaces", workspaceId, "implantations"],
      });
      void navigate({
        to: "/app/$workspaceId/implantacao/$implantationId",
        params: { workspaceId, implantationId: impl.id },
        search: { executar: true },
      });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const submit = () => {
    const parsed = createImplantationSchema.safeParse(buildInput());
    if (!parsed.success) {
      const map: Record<string, string> = {};
      for (const issue of parsed.error.issues) map[issue.path.join(".")] ??= issue.message;
      setErrors(map);
      toast.error("Confira os campos destacados.");
      return;
    }
    setErrors({});
    criar.mutate(parsed.data);
  };

  return (
    <>
      <PageHeader
        title="Nova implantação"
        description="Ao clicar em Criar, o sistema faz as etapas automáticas e deixa as manuais na lista, com link."
      />
      <div className="space-y-4">
        <Section title="1. Cliente e conta Meta">
          <FormField id="impl-name" label="Nome da implantação" error={errors.name}>
            <Input
              id="impl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="IA GENIX"
            />
          </FormField>
          <FormField
            id="impl-conn"
            label="Conta Meta que vai criar tudo"
            error={errors.connectionId}
          >
            <Choice
              id="impl-conn"
              value={connectionId}
              onChange={setConnectionId}
              placeholder="Escolha a conta conectada"
              options={userConnections.map((c) => ({ value: c.id, label: c.name }))}
            />
          </FormField>
        </Section>

        <Section
          title="2. Página do Facebook"
          hint="A Meta não permite criar página pelo sistema. Se ainda não existir, deixe em branco: vira tarefa com link."
        >
          <FormField id="impl-page" label="Página">
            <Choice
              id="impl-page"
              value={page}
              onChange={setPage}
              placeholder="Escolha a página"
              options={[
                { value: SEM_PAGINA, label: "Ainda não tenho (criar depois)" },
                ...(pages.data ?? []).map((p) => ({ value: p.metaId, label: p.name })),
              ]}
            />
          </FormField>
        </Section>

        <Section title="3. Business Manager">
          <FormField id="impl-bm" label="BM" error={errors["business.businessId"]}>
            <Choice
              id="impl-bm"
              value={bmChoice}
              onChange={setBmChoice}
              placeholder="Escolha"
              options={[
                { value: NOVO, label: "Criar um BM novo" },
                ...adminBms.map((b) => ({ value: b.id, label: `Usar: ${b.name}` })),
              ]}
            />
          </FormField>
          {bmChoice === NOVO ? (
            <>
              <FormField id="impl-bm-name" label="Nome do BM" error={errors["business.name"]}>
                <Input
                  id="impl-bm-name"
                  value={bmName}
                  onChange={(e) => setBmName(e.target.value)}
                  placeholder="IA GENIX"
                />
              </FormField>
              <FormField id="impl-bm-vertical" label="Segmento" error={errors["business.vertical"]}>
                <Choice
                  id="impl-bm-vertical"
                  value={vertical}
                  onChange={setVertical}
                  placeholder="Escolha o segmento"
                  options={META_BUSINESS_VERTICALS.map((v) => ({
                    value: v,
                    label: META_BUSINESS_VERTICAL_LABELS[v],
                  }))}
                />
              </FormField>
            </>
          ) : null}
        </Section>

        <Section
          title="4. Conta de anúncio"
          hint="Criada em reais (BRL) e no fuso de São Paulo. Não dá para mudar depois."
        >
          <FormField id="impl-acc" label="Conta">
            <Choice
              id="impl-acc"
              value={accountChoice}
              onChange={setAccountChoice}
              placeholder="Escolha"
              options={[
                { value: NOVO, label: "Criar uma conta nova no BM" },
                ...(accounts.data ?? []).map((a) => ({
                  value: a.metaId,
                  label: `Usar: ${a.name}`,
                })),
              ]}
            />
          </FormField>
          {accountChoice === NOVO ? (
            <FormField id="impl-acc-name" label="Nome da conta" error={errors["adAccount.name"]}>
              <Input
                id="impl-acc-name"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="IA GENIX · Principal"
              />
            </FormField>
          ) : null}
        </Section>

        <Section
          title="5. Site: pixel e domínio"
          hint="Deixe o domínio em branco se o cliente não tem site."
        >
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={pixelCreate}
              onChange={(e) => setPixelCreate(e.target.checked)}
            />
            Criar pixel no BM e liberar para a conta
          </label>
          {pixelCreate ? (
            <FormField id="impl-pixel" label="Nome do pixel" error={errors["pixel.name"]}>
              <Input
                id="impl-pixel"
                value={pixelName}
                onChange={(e) => setPixelName(e.target.value)}
                placeholder="IA GENIX · Site"
              />
            </FormField>
          ) : null}
          <FormField id="impl-domain" label="Domínio do site" error={errors.domain}>
            <Input
              id="impl-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="iagenixcloud.com"
            />
          </FormField>
        </Section>

        <Section
          title="6. Primeira campanha"
          hint="Vira um rascunho no assistente: você só escolhe o criativo e o público e publica (nasce pausada)."
        >
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={campCreate}
              onChange={(e) => setCampCreate(e.target.checked)}
            />
            Criar o rascunho da primeira campanha
          </label>
          {campCreate ? (
            <>
              <FormField id="impl-camp" label="Nome da campanha" error={errors["campaign.name"]}>
                <Input
                  id="impl-camp"
                  value={camp.name}
                  onChange={(e) => setCamp({ ...camp, name: e.target.value })}
                  placeholder="IA GENIX · Leads"
                />
              </FormField>
              <FormField id="impl-obj" label="Objetivo">
                <Choice
                  id="impl-obj"
                  value={camp.objective}
                  onChange={(v) => setCamp({ ...camp, objective: v })}
                  placeholder="Objetivo"
                  options={IMPLANTATION_OBJECTIVES.map((o) => ({
                    value: o,
                    label: OBJECTIVE_LABEL[o],
                  }))}
                />
              </FormField>
              <FormField id="impl-budget" label="Orçamento diário (R$)">
                <Input
                  id="impl-budget"
                  inputMode="decimal"
                  value={camp.daily}
                  onChange={(e) => setCamp({ ...camp, daily: e.target.value })}
                />
              </FormField>
              <FormField id="impl-link" label="Link do anúncio">
                <Input
                  id="impl-link"
                  value={camp.link}
                  onChange={(e) => setCamp({ ...camp, link: e.target.value })}
                  placeholder="https://iagenixcloud.com"
                />
              </FormField>
              <FormField id="impl-headline" label="Título">
                <Input
                  id="impl-headline"
                  value={camp.headline}
                  onChange={(e) => setCamp({ ...camp, headline: e.target.value })}
                  placeholder="Fale com a IA GENIX"
                />
              </FormField>
              <div className="sm:col-span-2">
                <FormField id="impl-text" label="Texto principal">
                  <Textarea
                    id="impl-text"
                    rows={3}
                    value={camp.primaryText}
                    onChange={(e) => setCamp({ ...camp, primaryText: e.target.value })}
                  />
                </FormField>
              </div>
            </>
          ) : null}
        </Section>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={criar.isPending}>
            <Rocket />
            Criar
          </Button>
        </div>
      </div>
    </>
  );
}
